import { ls } from '@/plugins/vue-ls.ts';
import LeadType from '@/entities/LeadType.js';
import Division from '@/entities/Division.js';

export enum SearchBarModelType {
    CUSTOMER = 'customer',
    LEAD = 'lead',
    VEHICLE = 'vehicle',
    NOTE = 'note',
    USER = 'user',
    RECENT = 'recent',
}

export enum SearchBarItemType {
    SEARCH = 'search',
    RESULT = 'result',
}

type idType = string | number | null | undefined;

interface SearchBarItemBase {
    id: idType;
    itemType: SearchBarItemType;
    title?: string | null;
    description?: string;
    subTitle?: string;
    other?: string;
    leadId?: string | number;
    createdAt?: string;
    icon?: string;
    status?: string | null;
    timestamp?: number;
}

export type CustomerSearchBarItem = SearchBarItemBase & {
    type: SearchBarModelType.CUSTOMER;
    accountId: number;
    userId: number;
};

export type NonCustomerSearchBarItem = SearchBarItemBase & {
    type: Exclude<SearchBarModelType, SearchBarModelType.CUSTOMER>;
};

export type SearchBarItemObj = CustomerSearchBarItem | NonCustomerSearchBarItem;

export class SearchBar {
    private static readonly STORAGE_KEY = 'search.results';

    private static ls() {
        return ls;
    }

    static all(): SearchBarItemObj[] {
        const ls = SearchBar.ls();
        const stored = ls?.get?.(SearchBar.STORAGE_KEY);

        if (!Array.isArray(stored)) {
            return [];
        }

        const items = stored as SearchBarItemObj[];
        const validItems = items.filter(
            (item) =>
                item.type !== SearchBarModelType.CUSTOMER ||
                (item.accountId != null && item.userId != null)
        );

        if (validItems.length !== items.length) {
            ls.set(SearchBar.STORAGE_KEY, validItems);
        }

        return validItems;
    }

    static set(searchBarItem: SearchBarItemObj): void {
        const ls = SearchBar.ls();
        const recentItems = SearchBar.all();
        const itemExists = recentItems.find((item) =>
            SearchBar.hasSameIdentity(item, searchBarItem)
        );

        // If existing -> update timestamp and merge data
        if (itemExists) {
            itemExists.timestamp = Date.now();
            Object.assign(itemExists, searchBarItem);
            ls.set(SearchBar.STORAGE_KEY, recentItems, 604800000);

            return;
        }

        searchBarItem.timestamp = Date.now();
        const updatedItems = [...recentItems, searchBarItem];
        const grouped = new Map<string, SearchBarItemObj[]>();

        for (const item of updatedItems) {
            const key = SearchBar.groupKey(item);

            if (!grouped.has(key)) {
                grouped.set(key, []);
            }

            grouped.get(key)!.push(item);
        }

        const pruned: SearchBarItemObj[] = [];

        for (const [, items] of grouped) {
            const maxItemsPerGroup = items[0]?.itemType === SearchBarItemType.SEARCH ? 2 : 4;
            items.sort((a, b) => (b.timestamp && a.timestamp ? b.timestamp - a.timestamp : 0));
            pruned.push(...items.slice(0, maxItemsPerGroup));
        }

        ls.set(SearchBar.STORAGE_KEY, pruned, 604800000); // Expire in 1 week if no activity
    }

    static setFromContextLead(contextLead: {
        id?: string | number;
        first_name?: string;
        last_name?: string;
        lead_type_id?: string | number;
        division_id?: string | number;
        created_at?: string;
        status?: string | null;
        [key: string]: any;
    }): void {
        if (!contextLead?.id) {
            return;
        }

        const vehicle = contextLead.getSoldOrFirstWantedVehicle();
        const vehicleStr = [vehicle?.make, vehicle?.model, vehicle?.year].filter(Boolean).join(' ');

        const mappedLead: SearchBarItemObj = {
            id: contextLead.id,
            leadId: contextLead.id,
            createdAt: contextLead.created_at,
            type: SearchBarModelType.LEAD,
            itemType: SearchBarItemType.RESULT,
            title: `${contextLead.first_name} ${contextLead.last_name}`,
            icon: LeadType.getIcon(contextLead.lead_type_id),
            subTitle: Division.getTranslation(contextLead.division_id) as string,
            description: vehicleStr,
            status: contextLead.status,
        };

        SearchBar.set(mappedLead);
    }

    static delete(
        id: string | number | null | undefined,
        type: SearchBarModelType,
        itemType: SearchBarItemType,
        userId?: number,
        accountId?: number
    ): void {
        const ls = SearchBar.ls();
        const existing = SearchBar.all();
        const filtered = existing.filter((item) => {
            if (item.id !== id || item.type !== type || item.itemType !== itemType) {
                return true;
            }

            if (item.type !== SearchBarModelType.CUSTOMER) {
                return false;
            }

            return !SearchBar.hasSameCustomerScope(item, userId, accountId);
        });

        ls.set(SearchBar.STORAGE_KEY, filtered);
    }

    private static hasSameIdentity(left: SearchBarItemObj, right: SearchBarItemObj): boolean {
        if (left.id !== right.id || left.type !== right.type || left.itemType !== right.itemType) {
            return false;
        }

        if (
            left.type !== SearchBarModelType.CUSTOMER ||
            right.type !== SearchBarModelType.CUSTOMER
        ) {
            return true;
        }

        return SearchBar.hasSameCustomerScope(left, right.userId, right.accountId);
    }

    private static groupKey(item: SearchBarItemObj): string {
        const key = `${item.type}:${item.itemType}`;

        if (item.type !== SearchBarModelType.CUSTOMER) {
            return key;
        }

        return `${key}:${Number(item.userId)}:${Number(item.accountId)}`;
    }

    private static hasSameCustomerScope(
        item: CustomerSearchBarItem,
        userId?: number,
        accountId?: number
    ): boolean {
        return (
            Number(item.userId) === Number(userId) && Number(item.accountId) === Number(accountId)
        );
    }
}
